import { useTranslation } from 'react-i18next';
import { useEffect, useState, Fragment } from 'react';
import {
  Button,
  Checkbox,
  IconButton,
  InputText,
  InputNumber,
} from '@teo/components';
import {
  Search,
  Pencil,
  ChevronUp,
  ChevronDown,
  ChevronDownFilled,
} from '@teo/components/icons';
import AddCategoryModal from './AddCategoryModal';
import { useGet } from '@/query';
import { parseFilter, newUid } from '@/util';
import PaginationNew from '@/components/DataTable/PaginationNew';
import EmptyTable from '@/components/DataTable/EmptyTable';
import { DataTable } from '@/components/DataTable/DataTable';
import TextWithTooltip from '@/pages/Protected/Manage/Competencies/TextWithTooltip';
import { axios } from '@/axios';
import { config } from '@/config';
import { Listbox, Transition } from '@headlessui/react';
import { clsx } from 'clsx';
import ComponentDeleteModal from '@/components/DeleteModal/ComponentDeleteModal';

export const CategoryTab = ({
  authUser,
  setRefetchTable,
  refetchTable,
  refetchListCategories,
}) => {
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  const [filter, setFilter] = useState('');
  const [selectedRow, setSelectedRow] = useState(new Set());
  const [isOpenAddModal, setIsOpenAddModal] = useState(false);
  const [result, setResult] = useState(null);

  const [count, setCount] = useState(10);
  const [maxPage, setMaxPage] = useState(1);
  const [selectMode, setSelectMode] = useState(true);

  const [cecategories, setCecategories] = useState(null);

  const [activeCategory, setActiveCategory] = useState(null);
  const [isOpenEdit, setIsOpenEdit] = useState(false);
  const [isRowOpen, setIsRowOpen] = useState(false);
  const [rowIndex, setRowIndex] = useState();

  const [isOpenDelModal, setIsOpenDelModal] = useState(false);

  const showOptions = [
    {
      value: 10,
      label: '10',
    },
    {
      value: 20,
      label: '20',
    },
    {
      value: 40,
      label: '40',
    },
    {
      value: 80,
      label: '80',
    },
  ];

  const [showType, setShowType] = useState(showOptions[0]);

  const { refetch: refetchCategories } = useGet('/competencecategories', {
    organisationId: `${authUser?.activeOrganisationId},OR,organisationId=null`,
    lang: authUser?.lang,
    count,
    offset,
    sort: 'name',
    q: parseFilter(filter),
    onSuccess: (result) => {
      setResult(result || {});
    },
  });

  useEffect(() => {
    if (result) {
      setCecategories(result?.data || {});
      const contentRange = result?.headers?.['content-range']
        ?.split(' ')?.[1]
        ?.split('/');
      const maxCount = contentRange ? Math.ceil(contentRange?.[1]) : 1;
      setMaxPage(Math.ceil(maxCount / count));
    }
  }, [result]);

  const page = Math.floor(offset / count) + 1;

  useEffect(() => {
    refetchCategories();
  }, [filter, refetchTable, offset]);

  const handleItemSelect = (key) => {
    const newSelected = new Set(selectedRow);
    if (
      (newSelected.has(key) && selectMode) ||
      (!newSelected.has(key) && !selectMode)
    ) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    setSelectedRow(newSelected);
  };

  const deleteCategory = () => {
    [...selectedRow]?.map((categoryId) => {
      axios
        .delete(`${config.backend}/competencecategory/${categoryId}`)
        .then(() => {
          setSelectedRow(new Set());
          setRefetchTable(newUid(20));
          setIsOpenDelModal(false);
        });
    });
  };

  if (!cecategories) return null;

  return (
    <>
      <div className="flex w-full flex-col sm:flex-row sm:items-center">
        <div className="py-8 pr-2 sm:w-1/2">
          <InputText
            value={filter}
            onChange={(ev) => {
              setOffset(0);
              setFilter(ev.target.value);
            }}
            Icon={Search}
            placeholder={t('pages.manage_competencies.search_placeholder_3')}
          />
        </div>
        <div className="relative z-[1] ml-auto flex flex-col justify-between lg:flex-row lg:items-center">
          <div className="flex flex-row sm:ml-auto">
            {selectedRow?.size > 0 && (
              <Button
                // onClick={deleteCategory}
                onClick={() => setIsOpenDelModal(true)}
                size="sm"
                disabled={selectedRow?.size < 1}
                variant="outline"
                className="!text-error-04"
              >
                {t('pages.manage_competencies.tab_categories.delete_button')}
              </Button>
            )}
            <Button
              onClick={() => {
                document.body.style.overflow = 'hidden';
                setIsOpenAddModal(true);
              }}
              size="sm"
              className="ml-2 flex items-center"
              variant="outline"
            >
              {t('pages.manage_competencies.tab_categories.new_category')}
            </Button>
          </div>
        </div>
      </div>
      {cecategories?.length ? (
        <>
          <div key={refetchTable}>
            {cecategories?.map((cecategory) => (
              <div
                key={refetchTable + '_' + cecategory.id}
                className="mb-3 overflow-hidden rounded-xl border bg-grey-01"
              >
                <div className="flex items-center py-4 px-6">
                  <div className="flex items-center">
                    <Checkbox
                      disabled={
                        authUser?.activeOrganisationId !== 18 &&
                        authUser?.activeOrganisationId !==
                          cecategory?.organisationId
                      }
                      id="selectedRow"
                      ariaLabel="test"
                      onChange={() => handleItemSelect(cecategory.id)}
                      checked={selectedRow.has(cecategory.id)}
                      key={cecategory.id + '_' + selectedRow.has(cecategory.id)}
                    />
                    <span className="overflow-hidden text-ellipsis pl-6 font-medium sm:text-xs md:text-base lg:text-base">
                      {cecategory.name}
                    </span>
                  </div>

                  <div className="ml-auto flex items-center justify-end">
                    <IconButton
                      Icon={Pencil}
                      variant="ghost"
                      onClick={() => {
                        setActiveCategory(cecategory);
                        document.body.style.overflow = 'hidden';
                        setIsOpenEdit(true);
                      }}
                      className="mr-2 hidden lg:block"
                      disabled={
                        authUser?.activeOrganisationId !== 18 &&
                        authUser?.activeOrganisationId !==
                          cecategory?.organisationId
                      }
                    />

                    <IconButton
                      Icon={
                        isRowOpen && rowIndex === cecategory.id
                          ? ChevronUp
                          : ChevronDown
                      }
                      variant="ghost"
                      onClick={() => {
                        setRowIndex(cecategory.id);
                        !isRowOpen && setIsRowOpen(!isRowOpen);
                        isRowOpen &&
                          cecategory.id == rowIndex &&
                          setIsRowOpen(!isRowOpen);
                      }}
                    />
                  </div>
                </div>
                {isRowOpen && rowIndex === cecategory.id && (
                  <div className=" bg-white px-2 pt-2">
                    <div className="pb-2">
                      <DataTable
                        key={refetchTable}
                        fetch={useGet.bind(null, '/categorycompetencedetails', {
                          competenceCategoryId: cecategory.id,
                          disabled: false,
                          sort: 'name',
                        })}
                        columns={[
                          {
                            addTitleOnMobile: true,
                            title: t('columns.code'),
                            id: 'code',
                            className:
                              'col-start-1 col-end-6 break-all row-start-1',
                            sortable: false,
                            classNameDesktop: 'w-[150px]',
                            render: (item) => {
                              return (
                                <p className="text-xs text-grey-06">
                                  {item?.code}
                                </p>
                              );
                            },
                          },
                          {
                            addTitleOnMobile: true,
                            title: t('columns.competence'),
                            id: 'competence',
                            className: 'col-start-1 col-end-10 row-start-2',
                            sortable: false,
                            render: (item) => {
                              return item.name;
                            },
                          },
                        ]}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          {maxPage > 1 && (
            <div className="flex w-full items-center justify-between">
              <div>
                <PaginationNew
                  setOffset={setOffset}
                  offset={offset}
                  count={count}
                  page={page}
                  maxPage={maxPage}
                />
              </div>
              <div className="flex items-center">
                <div className="ml-2 hidden items-center gap-1 lg:flex">
                  <p className="mr-1 text-sm text-grey-07">
                    {t('pagination.go_to_page')}
                  </p>
                  <InputNumber
                    mode="integer"
                    value={page}
                    onChange={(value) => {
                      if (value > 0) {
                        setOffset((value - 1) * count);
                      }
                    }}
                    min={1}
                    max={maxPage}
                    className="max-w-[50px]"
                  />
                </div>
                <div className="ml-2 flex items-center gap-1">
                  <p className="mr-1 text-sm text-grey-07">
                    {t('pagination.show')}
                  </p>
                  <div>
                    <Listbox
                      value={showType}
                      onChange={(newShowType) => {
                        setShowType(newShowType);
                        setCount(newShowType?.value);
                        setOffset(
                          Math.floor(offset / newShowType?.value) *
                            newShowType?.value
                        );
                      }}
                    >
                      {({ open }) => (
                        <div>
                          <Listbox.Button
                            className={clsx(
                              'flex items-center justify-between',
                              'rounded-md border border-solid outline outline-2',
                              'min-h-10 w-full min-w-[70px] px-2 text-base',
                              {
                                'border-secondary-04 bg-white outline-secondary-01':
                                  open,
                                'border-transparent bg-grey-transparent-01 text-grey-08 outline-transparent hover:border-grey-transparent-03 hover:bg-white hover:outline-grey-transparent-01 focus:border-secondary-04 focus:bg-white focus:outline-secondary-01':
                                  !open,
                              }
                            )}
                          >
                            <span className="mr-2 block truncate">
                              {!showType?.label ? '' : showType?.label}
                            </span>
                            <ChevronDownFilled
                              className={clsx(
                                'h-5 w-5',
                                'transition-transform',
                                open && 'rotate-180'
                              )}
                            />
                          </Listbox.Button>
                          <Transition
                            as={Fragment}
                            leave="transition-opacity"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                          >
                            <div className="listbox relative z-10">
                              <Listbox.Options className="absolute bottom-0 mt-1  flex max-h-40 w-full -translate-y-[42px] cursor-pointer flex-col gap-1 overflow-auto rounded-lg bg-white p-1 drop-shadow-s focus:outline-none">
                                {showOptions.map((option, optionIndex) => (
                                  <Listbox.Option
                                    key={optionIndex}
                                    className={({ active }) =>
                                      clsx(
                                        'rounded-md px-3 py-2 text-base text-black',
                                        active && 'bg-grey-transparent-02'
                                      )
                                    }
                                    value={option}
                                  >
                                    {({ selected }) => (
                                      <span
                                        className={clsx(
                                          'block',
                                          selected && 'font-medium'
                                        )}
                                      >
                                        <span>{option?.label}</span>
                                      </span>
                                    )}
                                  </Listbox.Option>
                                ))}
                              </Listbox.Options>
                            </div>
                          </Transition>
                        </div>
                      )}
                    </Listbox>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <EmptyTable
          template={2}
          text={t('empty_tenplate.categories_list')}
          btn_text={t('empty_tenplate.btn_add_category')}
          btnClick={() => {
            document.body.style.overflow = 'hidden';
            setIsOpenAddModal(true);
          }}
          filter={filter}
        />
      )}

      {isOpenDelModal && (
        <ComponentDeleteModal
          closeModal={() => setIsOpenDelModal(false)}
          setIsOpenDeleteModal={setIsOpenDelModal}
          title={t(
            selectedRow?.size > 1
              ? 'delete_modal.categories.title_m'
              : 'delete_modal.categories.title'
          )}
          heading={t(
            selectedRow?.size > 1
              ? 'delete_modal.categories.heading_m'
              : 'delete_modal.categories.heading'
          )}
          text={t(
            selectedRow?.size > 1
              ? 'delete_modal.categories.text_m'
              : 'delete_modal.categories.text'
          )}
          handleDelete={() => deleteCategory()}
        />
      )}

      {isOpenAddModal && (
        <AddCategoryModal
          closeModal={() => {
            document.body.style.overflow = 'unset';
            setIsOpenAddModal(false);
          }}
          setIsOpenAddModal={setIsOpenAddModal}
          setRefetchTable={setRefetchTable}
          authUser={authUser}
          refetchListCategories={refetchListCategories}
        />
      )}
      {isOpenEdit && (
        <AddCategoryModal
          closeModal={() => {
            document.body.style.overflow = 'unset';
            setIsOpenEdit(false);
          }}
          setIsOpenAddModal={setIsOpenEdit}
          setRefetchTable={setRefetchTable}
          authUser={authUser}
          activeCategory={activeCategory}
          edit={true}
        />
      )}
    </>
  );
};
